import React, { useEffect, useState } from 'react';
import { Checkbox, PriceBlock, PunchOut, Link } from '@hpi-sox/hpstellar-core';

import useTemplateTheme from '../../../hooks/useTemplateTheme';
import { Helpers } from '../../../core/src/helpers';
import { withError } from '../../../shared/components/error-boundary';
import usePage from '../../../hooks/usePage';
import { useMSRPProps } from '../../../hooks/useProductPrice';
import { deselectRecommendConfig, verifyPicks } from '../../util';

const handleSelection = (updateComponent, options, specIndex, optionIndex) => value => {
    updateComponent({ options }, state => {
        const { components } = state;
        const { baseConfig = {} } = components || {};
        const { specCategories, recommendedConfigs } = baseConfig;
        const updatedSpec = specCategories[specIndex] || {};
        const updateOption = updatedSpec.optionitems[optionIndex];
        if (updateOption) {
            updateOption.selected = value ? 'true' : 'false';
        }
        //whenever a selection is all preconfigs should be deselected
        deselectRecommendConfig(recommendedConfigs);
        //verify selection against server
        const componentChanges = { ...components, verifyingPicks: true };
        verifyPicks(componentChanges, updateComponent);
        //add flag to for verifying to prevent extra price and shiptdate api calls
        return { components: componentChanges };
    });
};

const toggleCheckBox = (setValue, onToggle) => e => {
    const value = e.currentTarget.checked;
    setValue(value);
    onToggle(value);
};

const CheckBoxContainer = ({ option, aligned, onToggle, GMPoints }) => {
    const {
        selected,
        sku,
        name,
        price,
        promoPrice,
        display,
        errorText,
        additionalText,
        gtmActions,
        subFrequency,
        pdpLinkText = 'View Details',
        pdpURL,
    } = option;
    const msrp = useMSRPProps();
    useEffect(() => {
        const checked = selected === 'true';
        //if value changes from verify picks, update it
        if (isChecked !== checked) {
            setValue(checked);
        }
    }, [selected]);
    const { typography } = useTemplateTheme(['typography']);
    const { primaryLinkClassName } = typography || {};
    const [isChecked, setValue] = useState(selected === 'true');
    const gmPoints = GMPoints && (GMPoints[sku] || '0.00');
    const subFrequencyText = Helpers.getSubFrequencyText(subFrequency);
    const translations = subFrequencyText ? { salePriceSuffix: subFrequencyText } : null;
    const regularPrice = Helpers.convertPriceToNumber(price);
    const salePrice = Helpers.convertPriceToNumber(promoPrice);
    return (
        display === 'true' && (
            <>
                <Checkbox
                    id={sku}
                    className="config-checkbox"
                    aligned={aligned}
                    checked={isChecked}
                    error={errorText}
                    onChange={toggleCheckBox(setValue, onToggle)}
                    gtmActions={gtmActions}
                >
                    <div className="config-checkbox-container">
                        <div>
                            {name}
                            {additionalText && (
                                <div
                                    className="offer-message"
                                    dangerouslySetInnerHTML={Helpers.createMarkup(additionalText)}
                                />
                            )}
                            {pdpURL && (
                                <Link
                                    className={`config-prd-link ${primaryLinkClassName ? ` ${primaryLinkClassName}` : ''}`}
                                    aria-label="aria-label placeholder"
                                    target="_blank"
                                    dense
                                    to={pdpURL}
                                    variation="body"
                                >
                                    {pdpLinkText}
                                </Link>
                            )}
                        </div>
                        {isChecked ? (
                            <div>{'Included In Price'}</div>
                        ) : (
                            <PriceBlock
                                className="priceBlock"
                                regularPrice={regularPrice}
                                salePrice={salePrice}
                                style="slim"
                                size="small"
                                translations={translations}
                                msrp={regularPrice > 0 ? msrp : undefined}
                            />
                        )}
                    </div>
                </Checkbox>
                {gmPoints && <PunchOut grossMargin={gmPoints} />}
            </>
        )
    );
};

export default withError(({ options = [], specIndex, aligned = 'left', GMPoints }) => {
    const { updateComponent } = usePage();
    return (
        <>
            {options.map((option, i) => (
                <CheckBoxContainer
                    key={i}
                    option={option}
                    onToggle={handleSelection(updateComponent, options, specIndex, i)}
                    specIndex={specIndex}
                    aligned={aligned}
                    GMPoints={GMPoints}
                />
            ))}
        </>
    );
});
